/* istanbul ignore file */
import { get, isEmpty } from 'underscore';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  usePreference,
  preferenceNames,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import { createOrUpdateAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { getODataConfig } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { uploadSuggestedImages, uploadTempImage } from './components/image-experience-utils';

import {
  getAssetGroupViewModelWithTempAssets,
  assetGroupViewModelToModel,
  assetGroupModelToViewModel,
} from './model';

export const useCreateTempAssetGroup = () => {
  const { scope } = useContext(ScopeContext);
  const preferenceService = useContext(PreferenceContext);
  const [, { update: updateDefaultImageIdString }] = usePreference(preferenceNames.TempImageIdString);
  const [imageIdString, setImageIdString] = useState('');
  const { send } = useWizardService();

  useEffect(() => {
    if (imageIdString && !isEmpty(preferenceService)) {
      updateDefaultImageIdString(imageIdString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIdString]);

  const createAssetGroupWithTempAssets = useCallback((assetGroupData, serverContext, campaignId) => {
    const {
      CurrentAccount: { Id: accountId },
      CurrentCustomer: { Id: customerId },
    } = serverContext;
    const suggestedImages = get(assetGroupData, ['Images', 'SuggestedImages'], []);
    const imageOdata = new ImageOData(customerId, accountId, getODataConfig(serverContext));

    let tempImageIdString = '';
    const defaultImagePromise = () => uploadTempImage(imageOdata, customerId, accountId).then((imageList) => {
      tempImageIdString = String(get(imageList, [0, 0, 'idString'], ''));
      return imageList;
    });
    let imagePromise = () => Promise.resolve([]);
    if (isEmpty(suggestedImages)) {
      imagePromise = defaultImagePromise;
    } else {
      imagePromise = () => uploadSuggestedImages(suggestedImages, imageOdata, customerId, accountId)
        .catch(() => defaultImagePromise()); // if suggested images upload fails, use default image
    }

    return imagePromise().then((loadedImageList) => {
      const assetGroupDataWithTempAssets = getAssetGroupViewModelWithTempAssets(assetGroupData);

      const assetGroupDataWithImages = {
        ...assetGroupDataWithTempAssets,
        Images: {
          ImageList: loadedImageList,
        },
      };
      return assetGroupViewModelToModel(assetGroupDataWithImages, serverContext, customerId, accountId)
        .then((bulkModel) => {
          const modelToSave = {
            ...bulkModel,
            Campaign: { Id: campaignId },
          };
          return createOrUpdateAssetGroup(modelToSave);
        })
        .then((assetGroupModel) => {
          // clean cache and remove cacheId from scope after asset group is saved
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { cache: undefined });
          scope.remove(['cacheId', 'email', 'signature']);
          scope.append({ assetGroupId: assetGroupModel.Id });
          scope.history.replace({
            pathname: scope.history.location.pathname,
            search: scope.stringify(),
          });
          scope.save();

          const { Images } = assetGroupModel;
          const imagesJSON = JSON.parse(Images);
          const formattedImages = imagesJSON.map(image => ({
            Asset: {
              Id: image.id,
              IdString: image.idString,
              SubType: image.subType,
              AssetType: 'Image',
              CropX: image.cropX,
              CropY: image.cropY,
              CropHeight: image.cropHeight,
              CropWidth: image.cropWidth,
            },
          }));

          if (tempImageIdString) {
            setImageIdString(tempImageIdString);
          }

          const savedAssetGroupData = assetGroupModelToViewModel({
            ...assetGroupModel,
            Images: formattedImages,
          }, tempImageIdString);

          return {
            onboardingUrl: savedAssetGroupData.FinalUrls[0],
            assetGroupData: {
              ...savedAssetGroupData,
              FinalUrlForRecommendation: savedAssetGroupData.FinalUrls[0],
            },
          };
        });
    });
  }, [scope, send]);

  return createAssetGroupWithTempAssets;
};
